import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Accordion } from '../../../core/components/ui';
import { useCitationFormatting } from '../hooks/useCitationFormatting';
import { ChatCitationHeader } from './ChatCitationHeader';

/**
 * Individual citation component with expandable snippet
 * Displays source information, snippet preview, and link to original source
 *
 * Memoized to prevent unnecessary re-renders in chat message list
 */
export const ChatCitation = memo(function ChatCitation({ citation, index, totalCount }) {
  const { t } = useTranslation();

  // Get formatted title with custom hook
  const rawTitle = citation.file || citation.title || t('chat.citation.sourceDefault', 'Source');
  const decodedTitle = useCitationFormatting(rawTitle, Infinity); // No truncation for full title
  const citationTitle = useCitationFormatting(rawTitle, 60); // Truncated for header

  // Memoize snippet processing
  const displaySnippet = useMemo(() => {
    const maxLength = 500;
    if (!citation.snippet) return '';

    return citation.snippet.length > maxLength
      ? citation.snippet.substring(0, maxLength) + '...'
      : citation.snippet;
  }, [citation.snippet]);

  // Memoize header component
  const header = useMemo(
    () => (
      <ChatCitationHeader
        sourceType={citation.source_type}
        title={citationTitle}
        score={citation.score}
        index={index}
        showIndex={totalCount > 1}
      />
    ),
    [citation.source_type, citation.score, citationTitle, index, totalCount]
  );

  return (
    <Accordion header={header} defaultExpanded={false}>
      {/* Full Title Section */}
      <div className="mb-3">
        <p className="text-xs icon-muted mb-1">
          <strong>{t('chat.citation.sourceLabel', 'Source')}:</strong>
        </p>
        <p className="text-xs text-default break-words">{decodedTitle}</p>
      </div>

      {/* Snippet Section */}
      {displaySnippet && (
        <div className="mb-2">
          <p className="text-xs icon-muted mb-1">
            <strong>{t('chat.citation.snippet', 'Snippet')}:</strong>
          </p>
          <p className="text-xs text-default whitespace-pre-wrap leading-relaxed break-words overflow-wrap-anywhere">
            {displaySnippet}
          </p>
        </div>
      )}

      {/* View Source Link */}
      <div className="flex gap-2 flex-wrap">
        <a
          href={citation.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary no-underline text-xs font-medium hover:underline break-all"
        >
          {t('chat.citation.viewSource', 'View Source')} ↗
        </a>
      </div>
    </Accordion>
  );
});

ChatCitation.propTypes = {
  citation: PropTypes.shape({
    url: PropTypes.string,
    file: PropTypes.string,
    title: PropTypes.string,
    source_type: PropTypes.string,
    snippet: PropTypes.string,
    score: PropTypes.number,
  }).isRequired,
  index: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
};
